import { Component, LOCALE_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { OrdersService } from '../../../core/services/orders/orders.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

registerLocaleData(localeEs);
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { BadgeColorPipe } from '../../../core/pipe/badge-color.pipe';
import { BadgeVehiclePipe } from '../../../core/pipe/badge-vehicle.pipe';
import { BadgeClientPipe } from '../../../core/pipe/badge-client.pipe';
import { DATE_PROVIDERS } from '../../../shared/shared-date.module';

interface OrderCard {
  data: any;
  stage: string;
  form: FormGroup;
  hasChanges: boolean;
  isUpdating: boolean;
  showEditForm: boolean;
  daysSinceUnconfirmed: number;
  confirmStatus: 'pending' | 'confirmed_no_changes' | 'confirmed_with_changes';
  previousFormValues?: any;
  editingField: 'vin' | 'plate' | null;
  deliveryUrgent: boolean;   // fecha prevista superada o a <= 3 días
  deliveryOverdue: boolean;  // fecha prevista ya pasó
  editFieldValue: string;
}

@Component({
  selector: 'app-orders-delivery',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule, TranslateModule, MatDatepickerModule, MatFormFieldModule,
    MatInputModule,
    BadgeColorPipe,
    BadgeVehiclePipe,
    BadgeClientPipe],
  providers: [...DATE_PROVIDERS, { provide: LOCALE_ID, useValue: 'es' }],
  templateUrl: './orders-delivery.component.html',
  styleUrl: './orders-delivery.component.css'
})
export class OrdersDeliveryComponent {
  // --- Modo multi-orden (mock / futuro batch) ---
  orders: OrderCard[] = [];
  supplierName: string = '';

  // --- Toolbar: búsqueda, ordenamiento, grilla ---
  searchText = '';
  sortMode: 'asc' | 'desc' | 'no_date_first' = 'asc';
  gridCols: 1 | 2 | 3 | 4 = 1;

  // --- Modo single-orden (public update-delivery legacy) ---
  orderData: any;
  isLoading: boolean = true;
  errorMessage: string = '';
  showImportantDates = false;
  clocks: any[] = [];
  stage?: any;
  deliveryForm: FormGroup;
  isUpdating: boolean = false;
  hasFormChanges: boolean = false;
  showEditForm: boolean = false;
  daysSinceUnconfirmed: number = 0;

  private isMock: boolean = false;
  isMultiMode: boolean = false;
  private bulkToken: string = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private orderService: OrdersService,
    private alertService: AlertService,
    private translateService: TranslateService
  ) {
    this.deliveryForm = this.fb.group({
      client_name: [''],
      delivery_expected_date: ['', Validators.required],
      supplier_reference: [''],
      vendor_reference: [''],
      vin: [''],
      license_plate: ['']
    });

    this.deliveryForm.valueChanges.subscribe(() => {
      this.checkFormChanges();
    });
  }

  ngOnInit() {
    // Asegurar idioma en vista pública (sin sesión, puede no estar seteado)
    if (!this.translateService.currentLang) {
      this.translateService.use('es');
    }

    // Cargar preferencia de grilla
    const savedCols = localStorage.getItem('supplier_grid_cols');
    if (savedCols && ['1', '2', '3', '4'].includes(savedCols)) {
      this.gridCols = +savedCols as 1 | 2 | 3 | 4;
    }

    this.isMock = this.route.snapshot.data['mock'] === true;
    const url = this.route.snapshot.url.map(s => s.path).join('/');
    const isSupplierUpdate = url.includes('supplier-update');

    if (isSupplierUpdate) {
      this.isMultiMode = true;
      this.bulkToken = this.route.snapshot.params['reference'];
      if (this.isMock) {
        this.loadMockOrders();
      } else {
        this.loadNotDeliveredOrders();
      }
    } else {
      this.loadPublicOrder();
    }
  }

  // =============================================
  // TOOLBAR: búsqueda, ordenamiento, grilla
  // =============================================

  get visibleOrders(): OrderCard[] {
    let result = this.orders;

    // Filtrar por búsqueda
    if (this.searchText.trim()) {
      const term = this.searchText.toLowerCase();
      result = result.filter(o => {
        const d = o.data;
        return (d.client_name || '').toLowerCase().includes(term)
          || (d.broker_reference || '').toLowerCase().includes(term)
          || (d.supplier_reference || '').toLowerCase().includes(term)
          || (d.vendor_reference || '').toLowerCase().includes(term)
          || (d.vehicle || '').toLowerCase().includes(term)
          || (d.vin || '').toLowerCase().includes(term)
          || (d.license_plate || '').toLowerCase().includes(term)
          || (d.reference || '').toLowerCase().includes(term);
      });
    }

    // Ordenar
    return this.sortOrders(result);
  }

  private sortOrders(orders: OrderCard[]): OrderCard[] {
    return [...orders].sort((a, b) => {
      const dateA = a.data.delivery_expected_date;
      const dateB = b.data.delivery_expected_date;

      if (this.sortMode === 'no_date_first') {
        if (!dateA && dateB) return -1;
        if (dateA && !dateB) return 1;
      }

      if (!dateA && !dateB) return 0;
      if (!dateA) return this.sortMode === 'asc' ? 1 : -1;
      if (!dateB) return this.sortMode === 'asc' ? -1 : 1;

      const diff = new Date(dateA).getTime() - new Date(dateB).getTime();
      return this.sortMode === 'desc' ? -diff : diff;
    });
  }

  toggleSort(): void {
    if (this.sortMode === 'asc') this.sortMode = 'desc';
    else if (this.sortMode === 'desc') this.sortMode = 'no_date_first';
    else this.sortMode = 'asc';
  }

  setGridCols(cols: 1 | 2 | 3 | 4): void {
    this.gridCols = cols;
    localStorage.setItem('supplier_grid_cols', String(cols));
  }

  clearSearch(): void {
    this.searchText = '';
  }

  // =============================================
  // MULTI-ORDEN (API real not-delivered)
  // =============================================

  private loadNotDeliveredOrders(): void {
    this.isLoading = true;
    this.orderService.getPublicNotDeliveredOrders(this.bulkToken).subscribe({
      next: (response: any) => {
        const ordersData = response.data || [];
        if (ordersData.length > 0) {
          this.supplierName = ordersData[0]?.supplier?.name || '';
          this.orders = ordersData.map((order: any) => this.buildOrderCard(this.mapOrderTableResource(order)));
        } else {
          this.orders = [];
          this.supplierName = '';
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        this.isLoading = false;
        if (error.code === 403 || error.originalError?.status === 403) {
          this.router.navigate(['/login']);
        } else {
          this.errorMessage = this.translateService.instant('swal_messages.error_occurred');
        }
      },
    });
  }

  private mapOrderTableResource(order: any): any {
    // OrderTableResource tiene campos planos en la raíz (no anidados en un objeto vehicle)
    const clientName = order.client?.fullname
      || ((order.client?.name || '') + ' ' + (order.client?.lastname || '')).trim()
      || '';
    return {
      reference: order.reference,
      broker_reference: order.broker_reference || '',
      client: order.client || {},
      client_name: clientName,
      stage: order.stage,
      type: order.type || '',
      vehicle: order.vehicle || '',               // string directo, ej: "Techno Mild Hybrid Auto..."
      vehicle_brand: order.vehicle_brand || '',
      vehicle_model: order.vehicle_model || '',
      vehicle_status: order.vehicle_status || '',  // "new" | "used"
      colour: order.colour || '',                  // raíz, no vehicle.colour
      supplier: order.supplier || {},
      supplier_reference: order.supplier_reference || '',
      vendor_reference: order.vendor_reference || '',
      vendor_quote_reference: order.vendor_quote_reference || '',
      delivery_expected_date: order.delivery_expected_date || '',
      delivery_status: order.delivery_status || '',
      delivery_city: order.delivery_city || '',
      notes: '',
      vin: order.vin || '',                        // raíz, no vehicle.vin
      license_plate: order.license_plate || '',    // raíz, no vehicle.license_plate
      duration: order.duration || '',
      total_kms: order.total_kms || '',
      monthly_fee_total: order.monthly_fee_total || '',
      contract_signed_date: order.contract_signed_date || '',
      delivery_forecast_confirmed_at: order.delivery_forecast_confirmed_at || null,
      delivery_forecast_undo_available: order.delivery_forecast_undo_available || false,
      rules: order.rules || { delivery_expected_date: 'w', delivery_status: 'w', vin: 'w', license_plate: 'w' },
    };
  }

  // =============================================
  // MULTI-ORDEN (mock fallback)
  // =============================================

  private loadMockOrders(): void {
    const mockData = [
      {
        client: { name: 'María', lastname: 'García López', type: 'individual', tax_id: '12345678A' },
        client_name: 'María García López',
        stage: 'offer_sent',
        vehicle: 'Volkswagen Golf 8 Life 1.5 TSI',
        vehicle_status: 'new',
        colour: 'Blanco Oryx',
        supplier: { name: 'Volkswagen Group España' },
        supplier_reference: 'VGE-90215',
        vendor_reference: 'VR-10034',
        delivery_expected_date: '2026-11-15T00:00:00.000Z',
        delivery_status: 'on_track',
        notes: '',
        vin: 'WVWZZZ1KZRW123456',
        license_plate: '1234 ABC',
        last_confirmed_at: '2026-09-02T00:00:00.000Z',
        rules: { client_name: 'w', delivery_expected_date: 'w', supplier_reference: 'w', vendor_reference: 'w', vin: 'w', license_plate: 'w' }
      },
      {
        client: { name: 'Carlos', lastname: 'Martínez Ruiz', type: 'individual', tax_id: '87654321B' },
        client_name: 'Carlos Martínez Ruiz',
        stage: 'sent',
        vehicle: 'SEAT León FR 1.5 eTSI 150cv',
        vehicle_status: 'new',
        colour: 'Gris Magnético',
        supplier: { name: 'Volkswagen Group España' },
        supplier_reference: 'VGE-90318',
        vendor_reference: 'VR-10051',
        delivery_expected_date: '2026-10-28T00:00:00.000Z',
        delivery_status: 'delayed',
        notes: 'Pendiente confirmación de color por parte del cliente',
        vin: 'VSSZZZ5FZTR098765',
        license_plate: '',
        last_confirmed_at: '2026-09-10T00:00:00.000Z',
        rules: { client_name: 'r', delivery_expected_date: 'w', supplier_reference: 'w', vendor_reference: 'w', vin: 'w', license_plate: 'w' }
      },
      {
        client: { name: 'Elena', lastname: 'Fernández Torres', type: 'company', tax_id: 'B12345678' },
        client_name: 'Transportes Fernández S.L.',
        stage: 'offer_created',
        vehicle: 'Škoda Octavia Combi 2.0 TDI 150cv',
        vehicle_status: 'new',
        colour: 'Azul Race',
        supplier: { name: 'Volkswagen Group España' },
        supplier_reference: 'VGE-90402',
        vendor_reference: '',
        delivery_expected_date: '2026-12-05T00:00:00.000Z',
        delivery_status: 'on_track',
        notes: 'Vehículo en producción, sin incidencias',
        vin: '',
        license_plate: '',
        last_confirmed_at: '2026-08-20T00:00:00.000Z',
        rules: { client_name: 'r', delivery_expected_date: 'w', supplier_reference: 'w', vendor_reference: 'w', vin: 'w', license_plate: 'w' }
      },
      {
        client: { name: 'Javier', lastname: 'López Sánchez', type: 'individual', tax_id: '45678901C' },
        client_name: 'Javier López Sánchez',
        stage: 'offer_sent',
        vehicle: 'Audi A3 Sportback 35 TFSI S tronic',
        vehicle_status: 'used',
        colour: 'Negro Mito',
        supplier: { name: 'Volkswagen Group España' },
        supplier_reference: 'VGE-90187',
        vendor_reference: 'VR-10089',
        delivery_expected_date: '2026-10-10T00:00:00.000Z',
        delivery_status: 'on_track',
        notes: '',
        vin: 'WAUZZZ8V0RA543210',
        license_plate: '5678 DEF',
        last_confirmed_at: '2026-09-14T00:00:00.000Z',
        rules: { client_name: 'w', delivery_expected_date: 'w', supplier_reference: 'r', vendor_reference: 'r', vin: 'r', license_plate: 'r' }
      }
    ];

    this.supplierName = mockData[0]?.supplier?.name || '';
    this.orders = mockData.map(data => this.buildOrderCard(data));
    this.isLoading = false;
  }

  private buildOrderCard(data: any): OrderCard {
    const form = this.fb.group({
      delivery_expected_date: [data.delivery_expected_date || '', Validators.required],
      delivery_status: [data.delivery_status || 'on_track'],
      notes: [data.notes || '']
    });

    // Determinar estado inicial según campos del backend
    let initialStatus: OrderCard['confirmStatus'] = 'pending';
    if (data.delivery_forecast_undo_available) {
      initialStatus = data.delivery_forecast_confirmed_at
        ? 'confirmed_no_changes'
        : 'confirmed_with_changes';
    }

    // Calcular urgencia de entrega (fecha superada o a <= 3 días)
    const { urgent, overdue } = this.calcDeliveryUrgency(data.delivery_expected_date, data.stage);

    const card: OrderCard = {
      data,
      stage: data.stage,
      form,
      hasChanges: false,
      isUpdating: false,
      showEditForm: false,
      daysSinceUnconfirmed: this.calcDays(data.delivery_forecast_confirmed_at),
      confirmStatus: initialStatus,
      editingField: null,
      editFieldValue: '',
      deliveryUrgent: urgent,
      deliveryOverdue: overdue,
    };

    // Aplicar rules
    this.applyRulesToForm(form, data.rules);

    // Detectar cambios
    form.valueChanges.subscribe(() => {
      card.hasChanges = this.checkCardChanges(card);
    });

    return card;
  }

  /**
   * Determina si la entrega es urgente (fecha prevista <= 3 días o ya pasó).
   * No aplica si el stage ya es vehicle_delivered o posterior.
   */
  private calcDeliveryUrgency(dateStr: string, stage: string): { urgent: boolean; overdue: boolean } {
    const deliveredStages = ['vehicle_predelivered', 'vehicle_delivered', 'contract_interrupted', 'vehicle_returned'];
    if (!dateStr || deliveredStages.includes(stage)) return { urgent: false, overdue: false };
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const expected = new Date(dateStr);
    expected.setHours(0, 0, 0, 0);
    if (isNaN(expected.getTime())) return { urgent: false, overdue: false };
    const diffDays = Math.floor((expected.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return {
      urgent: diffDays <= 3,   // 3 días o menos (incluye ya pasó)
      overdue: diffDays < 0,   // ya pasó la fecha
    };
  }

  markAsDelivered(card: OrderCard): void {
    if (this.isMock) return;
    card.isUpdating = true;
    this.orderService.updatePublicNotDeliveredOrder(
      this.bulkToken,
      card.data.reference,
      { stage: 'vehicle_delivered' }
    ).subscribe({
      next: (response: any) => {
        card.isUpdating = false;
        card.stage = 'vehicle_delivered';
        card.data.stage = 'vehicle_delivered';
        card.deliveryUrgent = false;
        card.deliveryOverdue = false;
        if (response.data) {
          this.updateCardFromResponse(card, response.data);
        }
        this.alertService.success('swal_messages.orders.order_updated');
      },
      error: (error: any) => {
        card.isUpdating = false;
        this.handleCardError(error, card);
      },
    });
  }

  private calcDays(dateStr: string | null): number {
    if (!dateStr) return -1;
    const now = new Date();
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return -1;
    return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  }

  private applyRulesToForm(form: FormGroup, rules: any): void {
    if (!rules) return;
    Object.keys(rules).forEach(key => {
      const ctrl = form.get(key);
      if (!ctrl) return;
      rules[key] === 'w' ? ctrl.enable() : ctrl.disable();
    });
  }

  private checkCardChanges(card: OrderCard): boolean {
    const curr = card.form.value;
    const orig = card.data;
    return (
      this.formatDateForComparison(curr.delivery_expected_date) !== this.formatDateForComparison(orig.delivery_expected_date) ||
      (curr.delivery_status || '') !== (orig.delivery_status || '') ||
      (curr.notes || '') !== (orig.notes || '')
    );
  }

  confirmCard(card: OrderCard): void {
    card.isUpdating = true;
    if (this.isMock) {
      setTimeout(() => {
        card.isUpdating = false;
        card.confirmStatus = 'confirmed_no_changes';
      }, 600);
      return;
    }
    // API real: POST confirm ("Sigue igual")
    this.orderService.confirmPublicNotDeliveredOrder(
      this.bulkToken,
      card.data.reference
    ).subscribe({
      next: (response: any) => {
        card.isUpdating = false;
        this.updateCardFromResponse(card, response.data);
      },
      error: (error: any) => {
        card.isUpdating = false;
        this.handleCardError(error, card);
      },
    });
  }

  openCardEdit(card: OrderCard): void {
    card.showEditForm = true;
  }

  cancelCardEdit(card: OrderCard): void {
    card.showEditForm = false;
    card.form.patchValue({
      delivery_expected_date: card.data.delivery_expected_date || '',
      delivery_status: card.data.delivery_status || 'on_track',
      notes: card.data.notes || ''
    }, { emitEvent: false });
    setTimeout(() => { card.hasChanges = false; });
  }

  updateCard(card: OrderCard): void {
    if (!card.hasChanges) return;
    card.isUpdating = true;

    // Guardar valores previos para rollback
    card.previousFormValues = {
      delivery_expected_date: card.data.delivery_expected_date,
      delivery_status: card.data.delivery_status,
      notes: card.data.notes
    };

    if (this.isMock) {
      setTimeout(() => {
        const formVal = card.form.getRawValue();
        card.data.delivery_expected_date = formVal.delivery_expected_date;
        card.data.delivery_status = formVal.delivery_status;
        card.data.notes = formVal.notes;
        card.isUpdating = false;
        card.hasChanges = false;
        card.showEditForm = false;
        card.confirmStatus = 'confirmed_with_changes';
      }, 600);
      return;
    }

    // API real: PATCH — enviar solo los campos que cambiaron
    const formVal = card.form.getRawValue();
    const orig = card.previousFormValues;
    const payload: any = {};
    const newDate = this.serializeDateForAPI(formVal.delivery_expected_date);
    const origDate = this.serializeDateForAPI(orig.delivery_expected_date);
    if (newDate !== origDate) {
      payload.delivery_expected_date = newDate || null;
    }
    if ((formVal.delivery_status || '') !== (orig.delivery_status || '')) {
      payload.delivery_status = formVal.delivery_status || null;
    }
    if ((formVal.notes || '') !== (orig.notes || '')) {
      payload.delivery_delay_notes = formVal.notes || null;
    }

    this.orderService.updatePublicNotDeliveredOrder(
      this.bulkToken,
      card.data.reference,
      payload
    ).subscribe({
      next: (response: any) => {
        card.isUpdating = false;
        card.hasChanges = false;
        card.showEditForm = false;
        this.updateCardFromResponse(card, response.data);
      },
      error: (error: any) => {
        card.isUpdating = false;
        this.handleCardError(error, card);
      },
    });
  }

  undoCard(card: OrderCard): void {
    if (this.isMock) {
      if (!card.previousFormValues) return;
      card.data.delivery_expected_date = card.previousFormValues.delivery_expected_date;
      card.data.delivery_status = card.previousFormValues.delivery_status;
      card.data.notes = card.previousFormValues.notes;
      card.form.patchValue({
        delivery_expected_date: card.previousFormValues.delivery_expected_date,
        delivery_status: card.previousFormValues.delivery_status,
        notes: card.previousFormValues.notes
      }, { emitEvent: false });
      card.previousFormValues = undefined;
      card.confirmStatus = 'pending';
      card.hasChanges = false;
      return;
    }
    // API real: POST undo
    card.isUpdating = true;
    this.orderService.undoPublicNotDeliveredOrder(
      this.bulkToken,
      card.data.reference
    ).subscribe({
      next: (response: any) => {
        card.isUpdating = false;
        card.showEditForm = false;
        card.hasChanges = false;
        this.updateCardFromResponse(card, response.data);
        // Resync form con datos restaurados
        card.form.patchValue({
          delivery_expected_date: card.data.delivery_expected_date || '',
          delivery_status: card.data.delivery_status || '',
          notes: card.data.notes || ''
        }, { emitEvent: false });
      },
      error: (error: any) => {
        card.isUpdating = false;
        if (error.code === 422) {
          this.alertService.warning('swal_messages.orders.nothing_to_undo');
          // Volver a estado inicial ya que no hay nada que deshacer
          card.confirmStatus = 'pending';
          card.data.delivery_forecast_undo_available = false;
        } else {
          this.handleCardError(error, card);
        }
      },
    });
  }

  openFieldEdit(card: OrderCard, field: 'vin' | 'plate'): void {
    card.editingField = field;
    card.editFieldValue = field === 'vin' ? (card.data.vin || '') : (card.data.license_plate || '');
  }

  cancelFieldEdit(card: OrderCard): void {
    card.editingField = null;
    card.editFieldValue = '';
  }

  saveFieldEdit(card: OrderCard): void {
    if (!card.editingField) return;
    const field = card.editingField;
    const value = card.editFieldValue.trim();

    if (this.isMock) {
      if (field === 'vin') card.data.vin = value;
      else card.data.license_plate = value;
      card.editingField = null;
      card.editFieldValue = '';
      return;
    }

    // API real: PATCH con campo individual
    const payload: any = {};
    if (field === 'vin') payload.vin = value;
    else payload.license_plate = value;

    this.orderService.updatePublicNotDeliveredOrder(
      this.bulkToken,
      card.data.reference,
      payload
    ).subscribe({
      next: (response: any) => {
        if (field === 'vin') card.data.vin = value;
        else card.data.license_plate = value;
        card.editingField = null;
        card.editFieldValue = '';
        if (response.data) {
          this.updateCardFromResponse(card, response.data);
        }
      },
      error: (error: any) => {
        card.editingField = null;
        card.editFieldValue = '';
        this.handleCardError(error, card);
      },
    });
  }

  /**
   * Actualiza la card con la respuesta del backend (OrderResource).
   * Determina confirmStatus según delivery_forecast_undo_available + delivery_forecast_confirmed_at.
   */
  private updateCardFromResponse(card: OrderCard, orderResource: any): void {
    // Actualizar datos de la card
    card.data.stage = orderResource.stage ?? card.data.stage;
    card.data.delivery_expected_date = orderResource.delivery_expected_date ?? card.data.delivery_expected_date;
    card.data.delivery_status = orderResource.delivery_status ?? card.data.delivery_status;
    card.data.delivery_delay_notes = orderResource.delivery_delay_notes ?? '';
    card.data.vin = orderResource.vin ?? card.data.vin;
    card.data.license_plate = orderResource.license_plate ?? card.data.license_plate;
    card.data.delivery_forecast_confirmed_at = orderResource.delivery_forecast_confirmed_at ?? null;
    card.data.delivery_forecast_undo_available = orderResource.delivery_forecast_undo_available ?? false;

    // Determinar estado de UI según los 2 campos del backend
    if (card.data.delivery_forecast_undo_available) {
      card.confirmStatus = card.data.delivery_forecast_confirmed_at
        ? 'confirmed_no_changes'    // undo_available + confirmed_at → "Confirmado sin cambios"
        : 'confirmed_with_changes'; // undo_available + no confirmed_at → "Nueva previsión confirmada"
    } else {
      card.confirmStatus = 'pending'; // sin snapshot → estado inicial
    }

    // Recalcular días y urgencia
    card.daysSinceUnconfirmed = this.calcDays(card.data.delivery_forecast_confirmed_at);
    const { urgent, overdue } = this.calcDeliveryUrgency(card.data.delivery_expected_date, card.data.stage);
    card.deliveryUrgent = urgent;
    card.deliveryOverdue = overdue;
  }

  private handleCardError(error: any, card: OrderCard): void {
    const code = error.code || error.originalError?.status;
    if (code === 403) {
      this.router.navigate(['/login']);
    } else if (code === 404) {
      // Pedido salió del bloque not_delivered — quitar de la lista
      this.orders = this.orders.filter(o => o !== card);
      this.alertService.warning('swal_messages.orders.not_found');
    } else {
      this.alertService.error(error.error || 'swal_messages.error_occurred');
    }
  }

  // =============================================
  // SINGLE-ORDEN (public update-delivery legacy)
  // =============================================

  private checkFormChanges(): void {
    if (!this.orderData) {
      this.hasFormChanges = false;
      return;
    }
    const curr = this.deliveryForm.value;
    const orig = {
      client_name: this.orderData.client_name || '',
      delivery_expected_date: this.orderData.delivery_expected_date || '',
      supplier_reference: this.orderData.supplier_reference || '',
      vendor_reference: this.orderData.vendor_reference || '',
      license_plate: this.orderData.license_plate || '',
      vin: this.orderData.vin || ''
    };
    this.hasFormChanges =
      this.formatDateForComparison(curr.delivery_expected_date) !== this.formatDateForComparison(orig.delivery_expected_date) ||
      (curr.client_name || '') !== (orig.client_name || '') ||
      (curr.supplier_reference || '') !== (orig.supplier_reference || '') ||
      (curr.vendor_reference || '') !== (orig.vendor_reference || '') ||
      (curr.license_plate || '') !== (orig.license_plate || '') ||
      (curr.vin || '') !== (orig.vin || '');
  }

  private formatDateForComparison(date: any): string {
    if (!date) return '';
    // Moment object
    if (typeof date.format === 'function') return date.format('YYYY-MM-DD');
    // Native Date
    if (date instanceof Date) return date.toISOString().split('T')[0];
    // String
    if (typeof date === 'string') return date.split('T')[0];
    return '';
  }

  private setFormData() {
    if (this.orderData) {
      this.deliveryForm.patchValue({
        client_name: this.orderData.client_name || '',
        delivery_expected_date: this.orderData.delivery_expected_date || '',
        supplier_reference: this.orderData.supplier_reference || '',
        vendor_reference: this.orderData.vendor_reference || '',
        license_plate: this.orderData.license_plate || '',
        vin: this.orderData.vin || ''
      }, { emitEvent: false });
      setTimeout(() => { this.hasFormChanges = false; });
    }
  }

  setVariables() {
    this.stage = this.orderData?.stage;
    this.setFormData();
    this.updateFieldAccessibility();
    this.calculateDaysSinceUnconfirmed();
  }

  private calculateDaysSinceUnconfirmed(): void {
    this.daysSinceUnconfirmed = this.calcDays(this.orderData?.last_confirmed_at);
  }

  loadPublicOrder() {
    this.route.params.subscribe(params => {
      const reference = params['reference'];
      this.route.queryParams.subscribe(queryParams => {
        const token = queryParams['token'];
        this.orderService.getPublicOrder(reference, token).subscribe({
          next: (response: any) => {
            this.orderData = response.data;
            this.setVariables();
            this.isLoading = false;
          },
          error: (error: any) => {
            this.isLoading = false;
            this.errorMessage = this.getErrorMessage(error);
          }
        });
      });
    });
  }

  getErrorMessage(error: any): string {
    if (error.status === 410) return 'Este enlace ya ha sido utilizado';
    if (error.status === 404) return 'El enlace ha expirado o es inválido';
    return this.translateService.instant('swal_messages.error_order');
  }

  confirmDelivery(): void {
    this.isUpdating = true;
    const reference = this.route.snapshot.params['reference'];
    const token = this.route.snapshot.queryParams['token'];
    this.orderService.updatePublicOrder(reference, token, { confirmed: true }).subscribe({
      next: () => {
        this.isUpdating = false;
        this.alertService.success('swal_messages.order_updated');
      },
      error: (error: any) => {
        this.isUpdating = false;
        this.alertService.error(this.getErrorMessage(error));
      }
    });
  }

  openEditForm(): void { this.showEditForm = true; }
  cancelEdit(): void { this.showEditForm = false; this.setFormData(); }

  updateOrder() {
    if (!this.hasFormChanges) {
      this.alertService.error('No hay cambios para actualizar');
      return;
    }
    if (!this.deliveryForm.get('delivery_expected_date')?.value) {
      this.alertService.error('Por favor, complete la fecha de entrega esperada');
      return;
    }
    this.isUpdating = true;
    const reference = this.route.snapshot.params['reference'];
    const token = this.route.snapshot.queryParams['token'];
    const updateData: any = {};

    const deliveryDate = this.deliveryForm.get('delivery_expected_date')?.value;
    if (deliveryDate) updateData.delivery_expected_date = this.serializeDateForAPI(deliveryDate);
    const clientName = this.deliveryForm.get('client_name')?.value?.trim();
    if (clientName) updateData.client_name = clientName;
    const supplierReference = this.deliveryForm.get('supplier_reference')?.value?.trim();
    if (supplierReference) updateData.supplier_reference = supplierReference;
    const vendorReference = this.deliveryForm.get('vendor_reference')?.value?.trim();
    if (vendorReference) updateData.vendor_reference = vendorReference;
    const licensePlate = this.deliveryForm.get('license_plate')?.value?.trim();
    if (licensePlate) updateData.license_plate = licensePlate;
    const vin = this.deliveryForm.get('vin')?.value?.trim();
    if (vin) updateData.vin = vin;

    this.orderService.updatePublicOrder(reference, token, updateData).subscribe({
      next: () => {
        this.isUpdating = false;
        this.hasFormChanges = false;
        this.showEditForm = false;
        this.alertService.success('swal_messages.order_updated');
        this.loadPublicOrder();
      },
      error: (error: any) => {
        this.isUpdating = false;
        if (error.status === 410) this.alertService.error('Este enlace ya ha sido utilizado para actualizar');
        else if (error.status === 404) this.alertService.error('El enlace ha expirado o es inválido');
        else this.alertService.error('Error al actualizar la orden');
      }
    });
  }

  /**
   * Serializa fecha para enviar al backend.
   * Mismo patrón que delivery-form: el datepicker con useUtc:true
   * devuelve un Moment UTC. Se serializa a ISO string que JSON.stringify
   * también usaría (ej: "2026-09-28T00:00:00.000Z").
   */
  private serializeDateForAPI(date: any): string {
    if (!date) return '';
    // Moment object (mat-datepicker con moment-adapter, useUtc: true)
    if (typeof date.toISOString === 'function') {
      return date.toISOString();
    }
    // String — devolver tal cual (ya viene del backend)
    if (typeof date === 'string') {
      return date;
    }
    return '';
  }

  updateFieldAccessibility() {
    const rules = this.orderData?.rules;
    if (!rules) return;
    ['client_name', 'delivery_expected_date', 'supplier_reference', 'vendor_reference', 'license_plate', 'vin'].forEach(key => {
      const ctrl = this.deliveryForm.get(key);
      if (!ctrl) return;
      rules[key] === 'w' ? ctrl.enable() : ctrl.disable();
    });
  }
}
